import { useCallback, useEffect, useRef, useState } from 'react';
import type { SourceReference } from '@shared/contract';
import { navigate } from '../App';
import { Questions } from '../components/Questions';
import { SummaryPanel } from '../components/SummaryPanel';
import { Transcript } from '../components/Transcript';
import { Visualizations } from '../components/Visualizations';
import { useMeeting } from '../hooks/useMeeting';
import { api } from '../lib/api';
import { formatDate, formatDuration, STATUS_LABELS } from '../lib/format';
import { uploadQueue, type QueueState } from '../lib/queue';
import { detectSourceType, isIOS, listMicrophones, SegmentRecorder } from '../lib/recorder';
import { clearSourceState, loadSourceState, saveSourceState } from '../lib/sourceState';

type LocalState = 'idle' | 'opening' | 'recording' | 'paused' | 'finishing';

const MIC_KEY = 'mr.mic';

/** Möte som ska börja spela in så fort vyn öppnas, satt när mötet just skapats. */
let autoStartId: string | null = null;

export function startOnOpen(meetingId: string): void {
  autoStartId = meetingId;
}

const clock = (ms: number) => new Date(ms).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

function readPref(key: string): string {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

export function MeetingView({ id }: { id: string }) {
  const { detail, setDetail, error, connection, lastEvent, deleted, refetch } = useMeeting(id);
  const [local, setLocal] = useState<LocalState>('idle');
  const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
  const [micId, setMicId] = useState(readPref(MIC_KEY));
  const [micName, setMicName] = useState('');
  const [level, setLevel] = useState(0);
  const [warning, setWarning] = useState<string | null>(null);
  const [stalled, setStalled] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [queue, setQueue] = useState<QueueState>(uploadQueue.getState());
  const [localPending, setLocalPending] = useState(0);
  const [summaryPending, setSummaryPending] = useState(false);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [title, setTitle] = useState('');
  const [copied, setCopied] = useState(false);
  const [, tick] = useState(0);
  const recorder = useRef<SegmentRecorder | null>(null);
  const wakeLock = useRef<{ release: () => Promise<void> } | null>(null);
  const hiddenAt = useRef<number | null>(null);

  /* ---------- Kö och statusar ---------- */

  useEffect(() => uploadQueue.subscribe(setQueue), []);
  useEffect(() => {
    void uploadQueue.pendingFor(id).then(setLocalPending);
  }, [id, queue]);

  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.type === 'summary.requested') setSummaryPending(true);
    if (lastEvent.type === 'summary.ready') {
      setSummaryPending(false);
      setSummaryFailed(false);
    }
    if (lastEvent.type === 'summary.failed') {
      setSummaryPending(false);
      setSummaryFailed(true);
    }
    if (lastEvent.type === 'progress.updated' && lastEvent.data?.error === 'transcription_failed') {
      setWarning('Ett ljudsegment kunde inte transkriberas efter flera försök. Ljudet är sparat.');
    }
  }, [lastEvent]);

  useEffect(() => {
    if (deleted) navigate('/');
  }, [deleted]);

  useEffect(() => {
    if (detail?.status === 'completed' && localPending === 0) clearSourceState(id);
  }, [detail?.status, localPending, id]);

  useEffect(() => {
    if (local !== 'recording') return;
    const t = window.setInterval(() => tick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, [local]);

  // Tydlig inspelningsindikator även i flikens titel.
  useEffect(() => {
    const base = detail?.title ? `${detail.title} · Svava Meet` : 'Svava Meet';
    document.title = local === 'recording' ? `● Spelar in · ${base}` : base;
    return () => {
      document.title = 'Svava Meet';
    };
  }, [local, detail?.title]);

  // Varna innan sidan stängs om inspelning pågår eller ljud inte laddats upp.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (local === 'recording' || local === 'paused' || localPending > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [local, localPending]);

  // Håll skärmen tänd under inspelning och upptäck när sidan dolts.
  const requestWakeLock = useCallback(async () => {
    try {
      const nav = navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } };
      if (nav.wakeLock && !wakeLock.current) wakeLock.current = await nav.wakeLock.request('screen');
    } catch {
      /* stöds inte överallt */
    }
  }, []);
  const releaseWakeLock = useCallback(() => {
    void wakeLock.current?.release().catch(() => undefined);
    wakeLock.current = null;
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt.current = Date.now();
        wakeLock.current = null;
      } else {
        if (local === 'recording') {
          void requestWakeLock();
          if (hiddenAt.current && Date.now() - hiddenAt.current > 2000 && isIOS()) {
            setWarning(
              'Sidan var dold en stund. På iPhone kan inspelningen ha avbrutits under tiden. Håll sidan öppen och skärmen tänd.',
            );
          }
        }
        hiddenAt.current = null;
        uploadQueue.kick();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [local, requestWakeLock]);

  useEffect(() => {
    void listMicrophones().then(setMics);
    const onChange = () => void listMicrophones().then(setMics);
    navigator.mediaDevices?.addEventListener?.('devicechange', onChange);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', onChange);
  }, []);

  // Släpp mikrofonen om användaren lämnar vyn.
  useEffect(
    () => () => {
      void recorder.current?.close();
      releaseWakeLock();
    },
    [releaseWakeLock],
  );

  /* ---------- Inspelning ---------- */

  async function openRecorder(): Promise<SegmentRecorder> {
    const state = loadSourceState(id);
    const sourceType = detectSourceType();
    const rec = new SegmentRecorder(state.next_sequence, state.offset_ms, {
      onSegment: async (seg) => {
        saveSourceState(id, {
          source_id: state.source_id,
          next_sequence: seg.sequence_number + 1,
          offset_ms: seg.offset_ms + seg.duration_ms,
        });
        await uploadQueue.enqueue(
          {
            meeting_id: id,
            source_id: state.source_id,
            source_type: sourceType,
            sequence_number: seg.sequence_number,
            started_at: seg.started_at,
            duration_ms: seg.duration_ms,
            offset_ms: seg.offset_ms,
            codec: seg.codec,
            sample_rate: seg.sample_rate,
            channels: seg.channels,
            device_name: seg.device_name.slice(0, 200),
          },
          seg.data,
        );
      },
      onLevel: setLevel,
      onError: (msg) => setWarning(msg),
      onStall: (since) => {
        setStalled(true);
        setWarning(`Inget ljud har kommit från mikrofonen sedan ${clock(since)}. Försöker starta om ljudet…`);
      },
      onRecover: (since, until) => {
        setStalled(false);
        setWarning(
          `Ljud saknas mellan ${clock(since)} och ${clock(until)}, till exempel för att datorn gick i viloläge. Inspelningen fortsätter nu.`,
        );
      },
    });
    await rec.open(micId || undefined);
    setMicName(rec.deviceName);
    void listMicrophones().then(setMics);
    recorder.current = rec;
    return rec;
  }

  async function start() {
    if (!detail) return;
    setActionError(null);
    setWarning(null);
    setStalled(false);
    setLocal('opening');
    try {
      const rec = await openRecorder();
      if (detail.status === 'created') await api.start(id);
      else if (detail.status === 'paused') await api.resume(id);
      rec.start();
      setLocal('recording');
      void requestWakeLock();
      void refetch();
    } catch (e) {
      await recorder.current?.close();
      recorder.current = null;
      setLocal('idle');
      const err = e as DOMException;
      setActionError(
        err.name === 'NotAllowedError'
          ? 'Mikrofonen är blockerad. Tillåt mikrofonen för sidan i webbläsarens inställningar och försök igen.'
          : err.name === 'NotFoundError' || err.name === 'OverconstrainedError'
            ? 'Den valda mikrofonen hittades inte. Välj en annan mikrofon.'
            : err.message,
      );
    }
  }

  // Ett nyss skapat möte börjar spela in direkt.
  useEffect(() => {
    if (detail?.status === 'created' && autoStartId === id && local === 'idle') {
      autoStartId = null;
      void start();
    }
  });

  async function pause() {
    setActionError(null);
    setStalled(false);
    await recorder.current?.pause();
    setLocal('paused');
    releaseWakeLock();
    await api.pause(id).catch((e) => setActionError(e.message));
    void refetch();
  }

  async function resume() {
    setActionError(null);
    try {
      await api.resume(id);
      recorder.current?.start();
      setLocal('recording');
      void requestWakeLock();
      void refetch();
    } catch (e) {
      setActionError((e as Error).message);
    }
  }

  async function finish() {
    if (!confirm('Avsluta mötet? Inspelningen stoppas och en slutlig sammanfattning skapas.')) return;
    setActionError(null);
    setLocal('finishing');
    await recorder.current?.close();
    recorder.current = null;
    releaseWakeLock();
    const state = loadSourceState(id);
    // Vänta en stund på att återstående segment laddas upp. Servern väntar sedan in resten.
    await uploadQueue.drain(id, 20_000);
    try {
      await api.finish(id, state.next_sequence > 0 ? { [state.source_id]: state.next_sequence - 1 } : {});
    } catch (e) {
      setActionError((e as Error).message);
    }
    setLocal('idle');
    void refetch();
  }

  async function remove() {
    if (!detail) return;
    if (!confirm(`Radera "${detail.title}" med ljud, transkription och sammanfattningar? Det går inte att ångra.`)) return;
    await recorder.current?.close();
    await api.deleteMeeting(id).catch((e) => setActionError(e.message));
    clearSourceState(id);
    navigate('/');
  }

  async function requestSummary() {
    setSummaryFailed(false);
    setSummaryPending(true);
    try {
      await api.requestSummary(id);
    } catch (e) {
      setSummaryPending(false);
      setActionError((e as Error).message);
    }
  }

  async function copy() {
    try {
      const text = await api.exportText(id, 'md');
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setActionError((e as Error).message);
    }
  }

  // Hänvisningar från före den slutliga transkriberingen pekar på löpande rader; då används tidpunkten.
  const jump = (ref: SourceReference) => {
    const lines = detail?.transcript ?? [];
    const target =
      lines.find((t) => t.id === ref.transcript_segment_id) ??
      lines.find((t) => t.start_ms <= ref.start_ms && ref.start_ms < t.end_ms) ??
      [...lines].reverse().find((t) => t.start_ms <= ref.start_ms);
    if (!target) return;
    setHighlight(target.id);
    setTimeout(() => setHighlight((h) => (h === target.id ? null : h)), 3000);
  };

  function startTitleEdit() {
    if (!detail) return;
    setTitle(detail.title);
    setEditingTitle(true);
  }

  async function saveTitle() {
    const next = title.trim();
    setEditingTitle(false);
    if (!detail || !next || next === detail.title) return;
    setDetail((d) => (d ? { ...d, title: next } : d));
    try {
      await api.renameMeeting(id, next);
    } catch (err) {
      setActionError((err as Error).message);
    }
    void refetch();
  }

  if (error && !detail) return <div className="page"><div className="alert error">{error}</div><a href="#/">Till möteslistan</a></div>;
  if (!detail) return <div className="center muted">Laddar möte…</div>;

  const status = detail.status;
  const isLive = status === 'recording' || status === 'paused';
  const remoteActive = isLive && local === 'idle';
  const recordedMs = recorder.current ? recorder.current.recordedMs : detail.duration_ms;
  const p = detail.progress;
  const waitingMs = Math.max(0, p.received_until_ms - p.transcribed_until_ms);

  return (
    <div className="page meeting">
      <div className="meeting-head">
        <a href="#/" className="back-link">← Alla möten</a>
        {editingTitle ? (
          <form
            className="row title-edit"
            onSubmit={(e) => {
              e.preventDefault();
              void saveTitle();
            }}
          >
            <input
              autoFocus
              value={title}
              maxLength={200}
              aria-label="Mötets titel"
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && setEditingTitle(false)}
            />
            <button className="btn primary small" disabled={!title.trim()}>
              Spara
            </button>
            <button type="button" className="btn ghost small" onClick={() => setEditingTitle(false)}>
              Avbryt
            </button>
          </form>
        ) : (
          <div className="title-row">
            <h1 title="Klicka för att byta namn" onClick={startTitleEdit}>
              {detail.title}
            </h1>
            <button className="btn ghost small" onClick={startTitleEdit}>
              Byt namn
            </button>
          </div>
        )}
        <p className="muted small">
          {formatDate(detail.started_at ?? detail.created_at)} · <span className={`pill status-${status}`}>{STATUS_LABELS[status]}</span>
        </p>
      </div>

      {/* Kontroller och status */}
      <section className={`card controls${local === 'recording' ? ' is-recording' : ''}`}>
        <div className="control-row">
          <div className="timer" aria-live="polite">
            {local === 'recording' && <span className="rec-dot" aria-label="Spelar in" />}
            {formatDuration(recordedMs)}
          </div>
          <div className="buttons">
            {(status === 'created' || remoteActive) && local === 'idle' && (
              <button className="btn primary big" onClick={start}>
                {status === 'created' ? 'Starta möte' : 'Fortsätt spela in här'}
              </button>
            )}
            {local === 'opening' && <button className="btn primary big" disabled>Startar mikrofon…</button>}
            {local === 'recording' && <button className="btn big" onClick={pause}>Pausa</button>}
            {local === 'paused' && <button className="btn primary big" onClick={resume}>Återuppta</button>}
            {(local === 'recording' || local === 'paused' || remoteActive) && (
              <button className="btn danger big" onClick={finish}>Avsluta</button>
            )}
            {local === 'finishing' && <button className="btn big" disabled>Avslutar…</button>}
          </div>
        </div>

        {(status === 'created' || remoteActive) && local === 'idle' && (
          <label className="mic-select">
            Mikrofon
            <select
              value={micId}
              onChange={(e) => {
                setMicId(e.target.value);
                try {
                  localStorage.setItem(MIC_KEY, e.target.value);
                } catch {
                  /* ignoreras */
                }
              }}
            >
              <option value="">Standardmikrofon</option>
              {mics.filter((m) => m.deviceId && m.deviceId !== 'default').map((m, i) => (
                <option key={m.deviceId} value={m.deviceId}>
                  {m.label || `Mikrofon ${i + 1}`}
                </option>
              ))}
            </select>
          </label>
        )}

        {status === 'created' && local === 'idle' && (
          <div className="notice">
            <strong>Om inspelningen.</strong> Ljudet spelas in när mötet startar och skickas för svensk
            transkribering och sammanfattning hos EU-baserade tjänster. Informera alla deltagare innan du startar och spela
            bara in när det är tillåtet.
          </div>
        )}

        <div className="status-grid">
          <Status
            label="Mikrofon"
            value={local === 'recording' || local === 'paused' ? micName || 'Aktiv' : 'Inte aktiv'}
            tone={local === 'recording' ? 'ok' : 'idle'}
          >
            {local === 'recording' && <span className="meter"><span style={{ width: `${Math.round(level * 100)}%` }} /></span>}
          </Status>
          <Status
            label="Inspelning"
            value={local === 'recording' ? (stalled ? 'Inget ljud, startar om' : 'Spelar in') : local === 'paused' ? 'Pausad' : remoteActive ? `${STATUS_LABELS[status]} (annan enhet eller flik)` : STATUS_LABELS[status]}
            tone={local === 'recording' ? (stalled ? 'warn' : 'rec') : 'idle'}
          />
          <Status
            label="Anslutning"
            value={!queue.online ? 'Offline, ljud sparas lokalt' : connection === 'open' ? 'Ansluten' : 'Återansluter…'}
            tone={!queue.online || connection !== 'open' ? 'warn' : 'ok'}
          />
          <Status
            label="Uppladdning"
            value={localPending ? `${localPending} segment väntar lokalt` : 'Allt ljud uppladdat'}
            tone={localPending > 2 ? 'warn' : 'ok'}
          />
        </div>

        {(isLive || status === 'finishing') && (
          <p className="muted small progress-line">
            Transkriberat fram till {formatDuration(p.transcribed_until_ms)} av {formatDuration(p.received_until_ms)} mottaget ljud.
            {p.segments_pending > 0 && ` ${p.segments_pending} segment väntar på transkribering (cirka ${Math.round(waitingMs / 1000)} s).`}
            {p.segments_failed > 0 && ` ${p.segments_failed} segment kunde inte transkriberas.`}
            {status === 'finishing' && ' Mötet slutbearbetas.'}
          </p>
        )}
        {detail.transcript_stage === 'final_pending' && (
          <p className="muted small progress-line">
            Hela ljudet transkriberas om för ett mer komplett transkript. Transkriptet och sammanfattningen ersätts när det
            är klart, oftast inom några minuter.
          </p>
        )}
        {detail.transcript_stage === 'final_failed' && (
          <div className="alert warn">
            Omtranskriberingen av hela ljudet misslyckades. Transkriptet och sammanfattningen bygger på den löpande
            transkriberingen.
          </div>
        )}

        {warning && <div className="alert warn">{warning} <button className="btn link small" onClick={() => setWarning(null)}>Stäng</button></div>}
        {queue.storageError && <div className="alert error">{queue.storageError}</div>}
        {queue.lastError && localPending > 0 && <div className="alert warn">{queue.lastError}</div>}
        {queue.rejected > 0 && (
          <div className="alert error">
            {queue.rejected} ljudsegment avvisades av servern och kan inte laddas upp.{' '}
            <button className="btn link small" onClick={() => uploadQueue.discardRejected()}>Ta bort</button>
          </div>
        )}
        {actionError && <div className="alert error">{actionError}</div>}

        {(local !== 'idle' || status === 'created') && (
          <p className="muted small limitation">
            Ljud som ännu inte laddats upp sparas tillfälligt i webbläsaren och skickas när anslutningen fungerar. Det är
            ingen garanti om sidan stängs eller om systemet avslutar webbläsaren.
            {isIOS() && ' På iPhone fungerar inspelning bara medan sidan är öppen och skärmen är upplåst. Lås inte telefonen och byt inte app under mötet.'}
          </p>
        )}
      </section>

      <div className="columns">
        <section className="card transcript-card">
          <div className="card-head">
            <h3>Transkription</h3>
            {status !== 'completed' && detail.transcript.some((t) => !t.is_final) && (
              <span className="muted small">Preliminär text</span>
            )}
          </div>
          <Transcript detail={detail} highlight={highlight} live={isLive} />
        </section>

        <div className="side">
          <SummaryPanel
            detail={detail}
            pending={summaryPending}
            failed={summaryFailed}
            onRequest={requestSummary}
            onJump={jump}
          />
          <Questions detail={detail} onJump={jump} onAsked={() => void refetch()} />
          <section className="card">
            <h3>Exportera</h3>
            <div className="row wrap">
              <a className="btn small" href={api.exportUrl(id, 'md')} download>Markdown</a>
              <a className="btn small" href={api.exportUrl(id, 'txt')} download>Text</a>
              <button className="btn small" onClick={copy}>{copied ? 'Kopierat!' : 'Kopiera'}</button>
              <button className="btn danger small" onClick={remove}>Radera möte</button>
            </div>
          </section>
        </div>
      </div>

      {status !== 'created' && detail.transcript.length > 0 && <Visualizations detail={detail} onJump={jump} />}
    </div>
  );
}

function Status({ label, value, tone, children }: { label: string; value: string; tone: 'ok' | 'warn' | 'rec' | 'idle'; children?: React.ReactNode }) {
  return (
    <div className={`status tone-${tone}`}>
      <span className="status-label">{label}</span>
      <span className="status-value">{value}</span>
      {children}
    </div>
  );
}
